import OfficeBuilding from 'mdi-material-ui/OfficeBuilding';
import SectionCard from '../../components/SectionCard';
import useMsftReferral from '../../hooks/useMsftReferral';
import DetailsGrid from '../../../../components/DetailsGrid/DetailsGrid';
import SolutionListItem from './SolutionListItem';
import useMsftSolutions from 'packages/cosell/src/pages/microsoft/hooks/useMsftSolutions';
import { useCallback, useMemo } from 'react';

/**
 * Displays a section showing Microsoft solutions associated with a referral.
 * Fetches the referral data and its associated solutions (if not already fetched), then enriches the solution
 * data with full solution details from the solutions API.
 *
 * @component
 * @param props
 * @param props.tackleId - The unique identifier for the referral to display solutions for
 *
 * @remarks
 * - Uses `useMsftReferral` to fetch the referral data with a summary of associated solutions
 * - Uses `useMsftSolutions` to fetch full solution data
 * - Solutions are displayed in a list
 *
 * @returns A card section displaying solution details, or null if referral data is not available
 */
const SolutionsSection = ({ tackleId }: { tackleId: string }) => {
  const { data: referral } = useMsftReferral({ tackleId });
  const solutions = referral?.details?.requirements?.solutions;
  const solutionIds = useMemo(
    () => solutions?.map((solution) => solution.id),
    [solutions],
  );

  const { data: solutionsWithAllData } = useMsftSolutions(
    {
      solutionID: solutionIds,
    },
    { enabled: !!solutionIds?.length },
  );

  const getFullSolutionData = useCallback(
    (solutionId: typeof solutionIds[number]) =>
      solutionsWithAllData?.solutions?.find(
        (solution) => solution.solutionId === solutionId,
      ) ?? null,
    [solutionsWithAllData],
  );

  if (!referral) {
    return null;
  }

  return (
    <SectionCard
      icon={
        <OfficeBuilding
          data-testid="office-building-icon"
          style={{ fill: '#253858' }}
        />
      }
      title="Solutions"
      bodyStyle={{ paddingBlock: '0', paddingBottom: '1.5rem' }}
    >
      <DetailsGrid>
        {solutions.map((solution) => {
          const fullSolutionData = getFullSolutionData(solution.id);
          return (
            <SolutionListItem
              key={solution.id}
              currency={solution.currency}
              id={solution.id}
              incentives={fullSolutionData?.incentives}
              name={solution.name}
              price={solution.price}
              publisherName={solution.publisherName}
              quantity={solution.quantity}
              solutionType={solution.solutionType}
            />
          );
        })}
      </DetailsGrid>
    </SectionCard>
  );
};

export default SolutionsSection;
